import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

const CaseEducationDetail = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: '项目概览', icon: 'ri-eye-line' },
    { id: 'comparison', label: '前后对比', icon: 'ri-contrast-2-line' },
    { id: 'features', label: '核心功能', icon: 'ri-function-line' },
    { id: 'implementation', label: '实施过程', icon: 'ri-roadmap-line' }
  ];

  const comparisonData = [
    {
      metric: '教学资源利用率',
      before: '各校区独立管理，利用率仅40%',
      after: '云端统一管理，利用率达95%',
      improvement: '提升138%',
      icon: 'ri-book-open-line'
    },
    {
      metric: '文档协作效率',
      before: '邮件传输，平均响应2-3天',
      after: '实时协作，即时响应',
      improvement: '提升200%',
      icon: 'ri-team-line'
    },
    {
      metric: '课件制作时间',
      before: '每个课件平均8小时',
      after: '模板化制作，平均2小时',
      improvement: '节省75%',
      icon: 'ri-time-line'
    },
    {
      metric: '作业批改效率',
      before: '人工批改，每份15分钟',
      after: 'AI辅助批改，每份3分钟',
      improvement: '提升400%',
      icon: 'ri-edit-line'
    },
    {
      metric: '存储成本',
      before: '各校区独立存储，年费180万',
      after: '云端统一存储，年费45万',
      improvement: '节省75%',
      icon: 'ri-cloud-line'
    },
    {
      metric: '系统覆盖范围',
      before: '仅覆盖总部及5个主要校区',
      after: '覆盖全国200+校区',
      improvement: '扩展4000%',
      icon: 'ri-global-line'
    }
  ];

  const coreFeatures = [
    {
      title: '智能课件制作',
      description: '基于AI的课件模板生成，支持多媒体内容整合和自动排版',
      benefits: ['模板库1000+套', '制作效率提升75%', '内容质量标准化'],
      icon: 'ri-slideshow-line'
    },
    {
      title: '云端协作编辑',
      description: '多人实时协作编辑文档，支持版本控制和权限管理',
      benefits: ['支持50人同时编辑', '版本自动保存', '权限精细控制'],
      icon: 'ri-edit-box-line'
    },
    {
      title: 'AI智能批改',
      description: 'AI辅助作业批改，支持主观题智能评分和个性化反馈',
      benefits: ['批改准确率95%', '个性化反馈', '学习轨迹分析'],
      icon: 'ri-robot-line'
    },
    {
      title: '移动学习支持',
      description: '全平台移动端支持，学生教师随时随地访问学习资源',
      benefits: ['离线学习支持', '多设备同步', '学习进度追踪'],
      icon: 'ri-smartphone-line'
    }
  ];

  const timeline = [
    { 
      week: '第1-2周', 
      phase: '需求调研与规划', 
      status: 'completed', 
      description: '深入了解教培集团现有教学流程和技术架构',
      deliverables: ['需求分析报告', '技术架构设计', '项目实施计划']
    },
    { 
      week: '第3-4周', 
      phase: '平台架构设计', 
      status: 'completed', 
      description: '设计云端文档管理平台架构和用户界面',
      deliverables: ['系统架构图', 'UI/UX设计稿', '数据库设计']
    },
    { 
      week: '第5-8周', 
      phase: '核心功能开发', 
      status: 'completed', 
      description: '开发文档管理、协作编辑、AI批改等核心功能',
      deliverables: ['文档管理系统', '协作编辑模块', 'AI批改引擎']
    },
    { 
      week: '第9-10周', 
      phase: '数据迁移与集成', 
      status: 'completed', 
      description: '迁移现有教学资源到云平台，集成第三方系统',
      deliverables: ['数据迁移完成', '系统集成测试', '性能优化']
    },
    { 
      week: '第11-12周', 
      phase: '试点测试', 
      status: 'completed', 
      description: '在5个试点校区进行功能测试和用户培训',
      deliverables: ['试点测试报告', '用户反馈收集', '系统优化']
    },
    { 
      week: '第13-14周', 
      phase: '全面部署', 
      status: 'completed', 
      description: '向全国200+校区推广部署，开展大规模培训',
      deliverables: ['全网部署完成', '培训体系建立', '运维支持']
    },
    { 
      week: '第15-16周', 
      phase: '上线运营', 
      status: 'completed', 
      description: '系统正式上线运营，提供持续技术支持',
      deliverables: ['系统正式上线', '运营数据监控', '持续优化']
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">项目背景</h3>
                
                <div className="space-y-6 mb-8">
                  <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                    <h4 className="font-bold text-red-900 mb-3 flex items-center">
                      <i className="ri-error-warning-line mr-2"></i>
                      面临的挑战
                    </h4>
                    <ul className="space-y-2 text-red-800">
                      <li className="flex items-start">
                        <i className="ri-close-circle-line text-red-500 mt-0.5 mr-2"></i>
                        多地分校文档管理分散，资源共享困难
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-line text-red-500 mt-0.5 mr-2"></i>
                        教学资源利用率低，重复建设严重
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-line text-red-500 mt-0.5 mr-2"></i>
                        作业批改效率低下，教师工作负担重
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-line text-red-500 mt-0.5 mr-2"></i>
                        学生学习体验不佳，缺乏个性化指导
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                    <h4 className="font-bold text-green-900 mb-3 flex items-center">
                      <i className="ri-lightbulb-line mr-2"></i>
                      解决方案
                    </h4>
                    <ul className="space-y-2 text-green-800">
                      <li className="flex items-start">
                        <i className="ri-check-circle-line text-green-500 mt-0.5 mr-2"></i>
                        构建云端统一文档管理平台
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-circle-line text-green-500 mt-0.5 mr-2"></i>
                        实现多人实时协作编辑功能
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-circle-line text-green-500 mt-0.5 mr-2"></i>
                        部署AI智能批改系统
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-circle-line text-green-500 mt-0.5 mr-2"></i>
                        提供全平台移动学习支持
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-8">
                  <h4 className="text-xl font-bold text-blue-900 mb-4">集团规模</h4>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">200+</div>
                      <div className="text-blue-800">校区数量</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">50万+</div>
                      <div className="text-blue-800">在校学生</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">8000+</div>
                      <div className="text-blue-800">教职员工</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">100+</div>
                      <div className="text-blue-800">课程体系</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/dd6021f2df039a6f6040f2add0bf8e7f.jpg"
                  alt="在线教育文档云平台"
                  className="w-full h-96 object-cover object-top rounded-xl shadow-2xl mb-8"
                />
                
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <i className="ri-trophy-line text-green-600 mr-2"></i>
                    项目成果
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">教学资源利用率</span>
                      <span className="font-bold text-green-600">提升 138%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">文档协作效率</span>
                      <span className="font-bold text-green-600">提升 200%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">课件制作时间</span>
                      <span className="font-bold text-green-600">节省 75%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">月活跃用户</span>
                      <span className="font-bold text-green-600">50万+</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">教育数字化转型成果</h3>
              <div className="grid md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-global-line text-purple-600 text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-purple-600 mb-2">200+</div>
                  <div className="text-gray-700">覆盖校区</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-line text-blue-600 text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-blue-600 mb-2">50万+</div>
                  <div className="text-gray-700">月活用户</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-book-open-line text-green-600 text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-green-600 mb-2">80%</div>
                  <div className="text-gray-700">资源利用率提升</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-money-dollar-circle-line text-orange-600 text-2xl"></i>
                  </div>
                  <div className="text-3xl font-bold text-orange-600 mb-2">135万</div>
                  <div className="text-gray-700">年节省成本</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'comparison':
        return (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-gray-900 mb-6">
                数字化转型前后对比
              </h3>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                云端文档管理平台为教培集团带来的全方位提升
              </p>
            </div>

            <div className="space-y-8">
              {comparisonData.map((item, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-6">
                      <i className={`${item.icon} text-3xl text-white`}></i>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900">{item.metric}</h4>
                  </div>

                  <div className="grid lg:grid-cols-3 gap-8">
                    <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-400">
                      <h5 className="font-bold text-red-900 mb-3 flex items-center">
                        <i className="ri-close-circle-line text-red-500 mr-2"></i>
                        改造前
                      </h5>
                      <p className="text-red-800">{item.before}</p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-400">
                      <h5 className="font-bold text-green-900 mb-3 flex items-center">
                        <i className="ri-check-circle-line text-green-500 mr-2"></i>
                        改造后
                      </h5>
                      <p className="text-green-800">{item.after}</p>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-400 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-blue-600 mb-2">
                          {item.improvement}
                        </div>
                        <div className="text-blue-800 font-semibold">
                          {item.metric.includes('成本') || item.metric.includes('时间') ? '节省' : '提升'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl p-8">
              <h3 className="text-3xl font-bold mb-8 text-center">投资回报率（ROI）分析</h3>
              <div className="grid md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">1200万</div>
                  <div className="text-green-100">初始投资</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">400万</div>
                  <div className="text-green-100">年节省成本</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">3年</div>
                  <div className="text-green-100">投资回收期</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">267%</div>
                  <div className="text-green-100">5年ROI</div>
                </div>
              </div>
              <div className="mt-8 p-6 bg-white/10 rounded-lg">
                <p className="text-sm">
                  <strong>成本节省来源：</strong>存储成本节省135万/年，教师工作效率提升节省人力成本180万/年，
                  课件制作效率提升节省外包成本60万/年，系统运维成本节省25万/年。
                </p>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-gray-900 mb-6">
                核心功能模块
              </h3>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                基于云计算和AI技术的智能教育文档管理平台
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
                      <i className={`${feature.icon} text-3xl text-white`}></i>
                    </div>
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h5 className="font-bold text-gray-900 mb-4 flex items-center">
                      <i className="ri-trophy-line text-green-500 mr-2"></i>
                      核心收益
                    </h5>
                    <div className="space-y-3">
                      {feature.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center">
                          <i className="ri-check-line text-green-500 mr-2"></i>
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">技术架构优势</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-cloud-line text-blue-600 text-3xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">云原生架构</h4>
                  <p className="text-gray-600 text-sm">支持弹性扩展，满足200+校区同时使用需求</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-brain-line text-green-600 text-3xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">AI智能引擎</h4>
                  <p className="text-gray-600 text-sm">智能内容生成、自动批改、个性化推荐</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-purple-600 text-3xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">安全保障</h4>
                  <p className="text-gray-600 text-sm">多层加密保护，确保教学数据安全</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">平台使用数据</h3>
              <div className="grid md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600 mb-2">50万+</div>
                  <div className="text-gray-600">月活跃用户</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2">1000万+</div>
                  <div className="text-gray-600">月处理文档</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-600 mb-2">99.9%</div>
                  <div className="text-gray-600">系统可用性</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">95%</div>
                  <div className="text-gray-600">用户满意度</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'implementation':
        return (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-gray-900 mb-6">
                16周实施时间轴
              </h3>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                从需求调研到全面上线，分阶段稳步推进项目实施
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-8 top-8 bottom-8 w-1 bg-gradient-to-b from-blue-200 to-purple-200"></div>
              
              <div className="space-y-8">
                {timeline.map((step, index) => (
                  <div key={index} className="relative flex items-start">
                    <div className="absolute left-4 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    
                    <div className="ml-20 bg-white rounded-xl shadow-lg p-8 flex-1 border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <span className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                            {step.phase}
                          </span>
                          <h4 className="text-xl font-bold text-gray-900 mt-2">{step.phase}</h4>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">时间</div>
                          <div className="font-bold text-purple-600">{step.week}</div>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-6">{step.description}</p>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h5 className="font-bold text-gray-900 mb-3">主要交付物</h5>
                        <div className="grid md:grid-cols-2 gap-3">
                          {step.deliverables.map((deliverable, idx) => (
                            <div key={idx} className="flex items-center">
                              <i className="ri-file-check-line text-green-500 mr-2"></i>
                              <span className="text-gray-700">{deliverable}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">项目团队构成</h3>
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h4 className="font-bold text-gray-900 mb-6 text-lg">技术团队</h4>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-user-star-line text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">项目经理</div>
                        <div className="text-gray-600 text-sm">1人 - 负责项目整体协调</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-code-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">开发工程师</div>
                        <div className="text-gray-600 text-sm">12人 - 前后端开发</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-brain-line text-purple-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">AI算法工程师</div>
                        <div className="text-gray-600 text-sm">4人 - AI功能开发</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-test-tube-line text-orange-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">测试工程师</div>
                        <div className="text-gray-600 text-sm">3人 - 质量保障</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-6 text-lg">教培集团团队</h4>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-admin-line text-red-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">信息技术总监</div>
                        <div className="text-gray-600 text-sm">1人 - 项目决策</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-graduation-cap-line text-yellow-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">教学总监</div>
                        <div className="text-gray-600 text-sm">5人 - 业务指导</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-user-line text-indigo-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">校区负责人</div>
                        <div className="text-gray-600 text-sm">20人 - 试点配合</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-customer-service-2-line text-pink-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">培训专员</div>
                        <div className="text-gray-600 text-sm">8人 - 用户培训</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-purple-600 via-blue-600 to-green-600 text-white py-24 overflow-hidden"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20education%20campus%20with%20digital%20learning%20technology%2C%20students%20and%20teachers%20using%20tablets%20and%20interactive%20displays%20in%20bright%20classrooms%2C%20cloud-based%20education%20platform%20visualization%20with%20colorful%20interface%20elements%2C%20innovative%20learning%20environment%20with%20glass%20buildings%20and%20green%20spaces&width=1920&height=800&seq=education-hero&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-blue-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="flex items-center mb-6">
              <span className="bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-semibold mr-4">
                成功案例详情
              </span>
              <span className="text-purple-200">教育培训</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              某教培集团在线教育文档云平台
            </h1>
            <p className="text-xl lg:text-2xl text-purple-100 mb-8 leading-relaxed">
              打造覆盖全国200+校区的云端教育文档管理平台，实现教学资源共享和协作效率大幅提升，月活跃用户超过50万
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold mb-1">200+</div>
                <div className="text-purple-200 text-sm">覆盖校区</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold mb-1">50万+</div>
                <div className="text-purple-200 text-sm">月活用户</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold mb-1">80%</div>
                <div className="text-purple-200 text-sm">资源利用率提升</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold mb-1">16周</div>
                <div className="text-purple-200 text-sm">实施周期</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="sticky top-0 z-10 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {renderTabContent()}
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="bg-white rounded-xl shadow-xl p-12">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-double-quotes-l text-4xl text-purple-600"></i>
            </div>
            <blockquote className="text-2xl text-gray-800 font-medium mb-8 leading-relaxed">
              "云端文档管理平台彻底改变了我们的教学模式。现在全国200多个校区的优质教学资源可以实时共享，教师协作效率大幅提升，学生的学习体验也得到了显著改善。这是我们数字化转型路上的重要里程碑。"
            </blockquote>
            <div className="flex items-center justify-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-line text-2xl text-white"></i>
              </div>
              <div className="text-left">
                <div className="font-bold text-gray-900">李总经理</div>
                <div className="text-gray-600">某教培集团 信息技术部</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            开启您的教育数字化转型
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            让我们为您的教育机构量身定制云端文档管理解决方案，实现教学资源的高效共享与协作
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact" className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-purple-50 transition-colors whitespace-nowrap cursor-pointer">
              免费咨询方案
            </a>
            <a href="/products" className="bg-purple-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-400 transition-colors whitespace-nowrap cursor-pointer border-2 border-white">
              查看产品详情
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseEducationDetail;
